import * as Sentry from "@sentry/react";
import { systemReport } from "@/lib/api";
import { useSessionStore } from "@/lib/auth";

/**
 * Nothing that breaks in the browser goes unrecorded.
 *
 * Two channels, both fed by `reportClientError`: the API's error record
 * (every signed-in error lands in the same table as server errors, with who
 * and where), and Sentry when a DSN is built in. Uncaught errors and
 * rejected promises are caught at the window, route errors by the router's
 * error component. One message is reported once per page load, so a render
 * loop does not become a thousand rows.
 */

const seen = new Set<string>();

export function initObservability() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn) {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      sendDefaultPii: false,
      tracesSampleRate: 0,
    });
    // Who, never their email: the id is enough to find them in the product.
    useSessionStore.subscribe((state) => {
      const user = state.user;
      Sentry.setUser(user ? { id: user.id } : null);
      Sentry.setTag("role", user?.role ?? "anonymous");
    });
  }

  window.addEventListener("error", (event) => {
    void reportClientError(event.error ?? event.message, "window");
  });
  window.addEventListener("unhandledrejection", (event) => {
    void reportClientError(event.reason, "promise");
  });
}

export async function reportClientError(error: unknown, where: string): Promise<void> {
  const message = messageOf(error);
  const stack = error instanceof Error ? error.stack : undefined;
  const key = `${where}|${message}`;
  if (seen.has(key)) return;
  seen.add(key);

  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error instanceof Error ? error : new Error(message), {
      tags: { where },
    });
  }
  // Only a signed-in browser can write to the API; the login page's errors
  // reach Sentry alone.
  if (!useSessionStore.getState().accessToken) return;
  try {
    await systemReport({
      body: {
        message: message.slice(0, 2000),
        stack: stack?.slice(0, 20_000),
        where: `${where} ${window.location.pathname}`.slice(0, 200),
        url: window.location.href.slice(0, 1000),
        userAgent: navigator.userAgent.slice(0, 500),
      },
    });
  } catch {
    // Reporting must never be the thing that breaks. It is in Sentry, or in
    // the console, and that is the most this browser can do.
  }
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
